<template>
    <vx-modal padding="standard" v-model:visible="visible" size="md">
        <template v-slot:header>{{ client.id ? 'Update' : 'Create' }} Client</template>
        <div>
            <!-- Form Errors -->
            <vx-alert v-if="error" color="danger" class="vx-mb-3" :closeable="false">
                {{ error }}
            </vx-alert>

            <div>
                <div class="vx-mb-3">
                    <vx-label>
                        Name
                        <template v-slot:description>Something your users will recognize and trust.</template>
                    </vx-label>
                    <vx-input
                        v-model="client.name"
                        :error="displayFirstError(validate.name)"
                        ref="inputRef"
                        type="text"
                        size="lg"
                    />
                </div>

                <div class="vx-mb-3">
                    <vx-label>
                        Redirect URL
                        <template v-slot:description>Your application's authorization callback URL.</template>
                    </vx-label>
                    <vx-input
                        v-model="client.redirect"
                        :error="displayFirstError(validate.redirect)"
                        type="text"
                        size="lg"
                    />
                </div>

                <div v-if="!client.id" class="vx-flex vx-items-start vx-justify-between vx-gap-4">
                    <div class="vx-flex-grow">
                        <vx-label size="md">Confidential</vx-label>
                        <div class="vx-text-sm vx-text-slate-500">
                            Require the client to authenticate with a secret. Confidential clients can hold credentials
                            in a secure way without exposing them to unauthorized parties. Public applications, such as
                            native desktop or JavaScript SPA applications, are unable to hold secrets securely.
                        </div>
                    </div>
                    <div class="flex-shrink-0">
                        <vx-toggle v-model="client.confidential" size="lg" class="vx-grow"></vx-toggle>
                    </div>
                </div>
            </div>
        </div>
        <template v-slot:footer>
            <div class="vx-flex vx-flex-col-reverse vx-gap-4 vx-justify-between lg:vx-flex-row">
                <vx-button color="muted" size="lg" @click="visible = false"> Cancel </vx-button>

                <vx-button
                    class="vx-grow"
                    :loading="loading"
                    @click.prevent="submit"
                    color="primary"
                    data-test="submit-button"
                    size="lg"
                    type="button"
                >
                    {{ client.id ? 'Update' : 'Create' }}
                </vx-button>
            </div>
        </template>
    </vx-modal>

    <vx-modal class="vx-max-w-lg" padding="standard" :visible="!!secret" @update:visible="secret = null">
        <template v-slot:header> Client Secret </template>

        <template v-slot:subheader>
            Your client secret has been created. Store it somewhere safe. You won't be able to see the secret again.
        </template>

        <vx-alert color="info" :closeable="false">
            Here is your new client secret.
            <strong>This is the only time it will be shown so don't lose it!</strong>
            You may now use this secret to make API requests.
        </vx-alert>
        <vx-popover hover arrow placement="top">
            <div
                @click.prevent="copySecret()"
                data-test="copy-token"
                class="vx-font-mono vx-break-all vx-cursor-pointer vx-p-4 vx-bg-white vx-rounded-sm vx-mt-4"
            >
                {{ secret }} <font-awesome-icon :icon="faCopy"></font-awesome-icon>
            </div>
            <template v-slot:content> Click to Copy </template>
        </vx-popover>

        <template v-slot:footer>
            <div class="vx-flex vx-flex-col-reverse vx-gap-4 lg:vx-flex-row">
                <vx-button
                    @click.prevent="secret = null"
                    tabindex="1"
                    type="button"
                    class="vx-grow lg:vx-w-20 lg:vx-grow-0"
                    size="lg"
                    color="muted"
                >
                    Close
                </vx-button>
                <vx-button @click.prevent="copySecret()" type="button" size="lg" block color="primary">
                    Copy Secret <font-awesome-icon :icon="faCopy"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { VxAlert, VxButton, VxLabel, VxInput, VxModal, VxToggle, VxPopover } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { displayFirstError } from '~/utils/validation';
import { nextTick, ref, watch } from 'vue';
import { maxLength, required } from '@vuelidate/validators';
import axios from '~/services/axios';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import { useToasted } from '~/composables/useToasted';
import { faCopy } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useCopyText } from '~/composables/useCopyText';

const emit = defineEmits(['saved']);
const toasted = useToasted();

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});
const defaultClient = () => {
    return {
        name: '',
        redirect: '',
        confidential: true,
    };
};
const client = defineModel('client', {
    type: Object,
});

const validate = useVuelidate(
    {
        name: { required, maxLength: maxLength(191) },
        redirect: { required },
    },
    client
);

const loading = ref(false);
const error = ref();
const inputRef = ref();
const secret = ref(null);

const { copyText } = useCopyText();

const copySecret = () => {
    copyText(secret.value);
};

watch(visible, async (visible) => {
    if (!visible) {
        client.value = defaultClient();
        error.value = '';
        validate.value.$reset();
    } else {
        await nextTick();
        inputRef.value.field?.focus?.();
    }
});

const submit = async () => {
    const isValid = await validate.value.$validate();

    if (!isValid || loading.value) {
        return;
    }

    loading.value = true;

    const method = client.value.id ? 'put' : 'post';
    const uri = client.value.id ? `/oauth/clients/${client.value.id}` : '/oauth/clients';
    const data = client.value.id ? { name: client.value.name, redirect: client.value.redirect } : { ...client.value };

    axios[method](uri, data)
        .then((response) => {
            emit('saved');
            secret.value = response.data?.plainSecret;
            visible.value = false;
            toasted.global.saved_successfully();
        })
        .catch((e) => {
            if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
                error.value = 'Something went wrong.';
            } else {
                error.value = e.response?.data?.message || 'Something went wrong.';
            }
        })
        .finally(() => {
            loading.value = false;
        });
};
</script>
